/**
 * Everything a signed-in person does to their own credentials, beside the
 * sign-in itself (lib/auth.tsx): choose a password from a link, confirm
 * an email address, and set up the authenticator app their Company
 * requires.
 *
 * All of it is between the browser and Firebase, and none of it needs the
 * API. Before this module the Firebase calls were made from the screens —
 * the two-factor gate, the reset page, the confirm page — and each read
 * Firebase's error codes itself, five places in all. Firebase is now
 * imported here, in lib/auth.tsx and lib/firebase.ts, and nowhere else;
 * a screen asks for an action and, when it fails, for one reason from a
 * short list of things a person can do something about.
 */
import {
  applyActionCode,
  confirmPasswordReset,
  EmailAuthProvider,
  multiFactor,
  reauthenticateWithCredential,
  TotpMultiFactorGenerator,
  verifyPasswordResetCode,
  type User,
} from "firebase/auth";
import { auth } from "./firebase";

/** Why a credential action failed, in terms of what to tell the person. */
export type CredentialFailure =
  | "wrong_password"
  | "wrong_code"
  | "too_many_attempts"
  | "weak_password"
  | "dead_link"
  | "needs_recent_sign_in"
  | "unverified_email"
  | "unknown";

const FAILURES: Record<string, CredentialFailure> = {
  "auth/invalid-credential": "wrong_password",
  "auth/wrong-password": "wrong_password",
  "auth/user-not-found": "wrong_password",
  "auth/invalid-verification-code": "wrong_code",
  "auth/too-many-requests": "too_many_attempts",
  "auth/weak-password": "weak_password",
  "auth/expired-action-code": "dead_link",
  "auth/invalid-action-code": "dead_link",
  "auth/requires-recent-login": "needs_recent_sign_in",
  "auth/unverified-email": "unverified_email",
};

/** The reason behind any error a sign-in or credential action threw. */
export function credentialFailure(err: unknown): CredentialFailure {
  const code =
    typeof err === "object" && err && "code" in err ? String(err.code) : "";
  return FAILURES[code] ?? "unknown";
}

function configuredAuth() {
  if (!auth) throw new Error("Firebase Auth is not configured");
  return auth;
}

/** The address a password link is for; throws when the link is spent. */
export const readPasswordLink = (oobCode: string): Promise<string> =>
  verifyPasswordResetCode(configuredAuth(), oobCode);

export const choosePassword = (oobCode: string, password: string) =>
  confirmPasswordReset(configuredAuth(), oobCode, password);

/**
 * A code works once, and a page mounted twice in development would spend
 * it on the first mount and read the second as a dead link.
 */
const applied = new Map<string, Promise<void>>();

/** Spend an email-confirmation link, once however often it is asked. */
export function confirmEmailLink(oobCode: string): Promise<void> {
  let pending = applied.get(oobCode);
  if (!pending) {
    const firebase = configuredAuth();
    pending = applyActionCode(firebase, oobCode).then(async () => {
      // A tab already signed in refreshes its token, so the setup it goes
      // back to sees the address as confirmed. A browser signed in to
      // nothing has no token to refresh, and the link still did its job.
      const current = firebase.currentUser;
      if (!current) return;
      try {
        await current.reload();
        await current.getIdToken(true);
      } catch {
        // The setup screen reloads the account itself on Continue.
      }
    });
    applied.set(oobCode, pending);
  }
  return pending;
}

export const hasAuthenticator = (user: User): boolean =>
  multiFactor(user).enrolledFactors.length > 0;

/** An authenticator app half set up: shown to the app, then proved. */
export interface AuthenticatorSetup {
  /** The otpauth:// link a QR code draws. */
  qrUrl: string;
  /** The same secret, for an app that cannot scan. */
  key: string;
  /** Prove the app took with its first code; refreshes the token. */
  enroll(code: string): Promise<void>;
}

export async function startAuthenticatorSetup(
  user: User,
): Promise<AuthenticatorSetup> {
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  return {
    qrUrl: secret.generateQrCodeUrl(user.email ?? "", "HireOS"),
    key: secret.secretKey,
    async enroll(code: string) {
      await multiFactor(user).enroll(
        TotpMultiFactorGenerator.assertionForEnrollment(
          secret,
          code.replace(/\s/g, ""),
        ),
        "Authenticator app",
      );
      await user.getIdToken(true);
    },
  };
}

/**
 * Firebase will only add a second factor to a sign-in made recently, so a
 * session that has been open a while proves it is still its owner first.
 */
export async function confirmPassword(user: User, password: string) {
  await reauthenticateWithCredential(
    user,
    EmailAuthProvider.credential(user.email ?? "", password),
  );
}

/** Whether the address is confirmed now; refreshes the token when it is. */
export async function emailConfirmed(user: User): Promise<boolean> {
  await user.reload();
  if (!user.emailVerified) return false;
  await user.getIdToken(true);
  return true;
}
